// After Action Review (AAR) — strukturert etter-øvelses/-oppdrags-gjennomgang
// generert fra en hendelses-tidslinje (audit-logg + tidsmaskin er kildene). Følger
// den anerkjente AAR-formen: hva var planen, hva skjedde, hvorfor, hva beholder/
// forbedrer vi. Ren + deterministisk (ingen sanntidsklokke) — tar inn tidsstempler,
// gir struktur + Markdown-rapport ut. LLM kan senere berike «hvorfor»-analysen.
function tally(items) {
    const m = new Map();
    for (const k of items)
        m.set(k, (m.get(k) ?? 0) + 1);
    return [...m.entries()]
        .map(([key, count]) => ({ key, count }))
        .sort((a, b) => b.count - a.count || a.key.localeCompare(b.key));
}
/**
 * Bygg en strukturert AAR fra input. Tidslinja sorteres kronologisk; nøkkel-
 * hendelser er de med vekt ≥ 2 (fallback: de 8 mest vektede) — deterministisk.
 */
export function generateAar(input, keyLimit = 8) {
    const timeline = [...input.events].sort((a, b) => a.tsMs - b.tsMs);
    const weighted = [...timeline].sort((a, b) => (b.weight ?? 0) - (a.weight ?? 0) || a.tsMs - b.tsMs);
    const flagged = weighted.filter((e) => (e.weight ?? 0) >= 2);
    const keyEvents = (flagged.length ? flagged : weighted.slice(0, keyLimit)).slice(0, keyLimit);
    const objectives = input.objectives ?? [];
    return {
        title: input.title,
        unit: input.unit,
        startMs: input.startMs,
        endMs: input.endMs,
        durationMin: Math.max(0, Math.round((input.endMs - input.startMs) / 60000)),
        eventCount: timeline.length,
        timeline,
        keyEvents,
        byKind: tally(timeline.map((e) => e.kind)),
        byActor: tally(timeline.filter((e) => e.actor).map((e) => e.actor)),
        objectives,
        objectivesMet: objectives.filter((o) => o.met).length,
        objectivesTotal: objectives.length,
        sustain: input.sustain ?? [],
        improve: input.improve ?? [],
    };
}
function hhmmUtc(ms) {
    const d = new Date(ms);
    return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}Z`;
}
/** Rendre AAR til en lesbar Markdown-rapport (til deling/arkiv/utskrift). */
export function aarToMarkdown(aar) {
    const L = [];
    L.push(`# AAR — ${aar.title}`);
    L.push(`**Enhet:** ${aar.unit}  •  **Varighet:** ${aar.durationMin} min  •  **Hendelser:** ${aar.eventCount}`);
    L.push('');
    if (aar.objectivesTotal) {
        L.push(`## Måloppnåelse (${aar.objectivesMet}/${aar.objectivesTotal})`);
        for (const o of aar.objectives)
            L.push(`- [${o.met ? 'x' : ' '}] ${o.text}${o.note ? ` — ${o.note}` : ''}`);
        L.push('');
    }
    if (aar.keyEvents.length) {
        L.push('## Nøkkelhendelser');
        for (const e of aar.keyEvents)
            L.push(`- **${hhmmUtc(e.tsMs)}** [${e.kind}] ${e.summary}${e.actor ? ` (${e.actor})` : ''}`);
        L.push('');
    }
    L.push('## Tidslinje');
    for (const e of aar.timeline)
        L.push(`- ${hhmmUtc(e.tsMs)} [${e.kind}] ${e.summary}${e.actor ? ` — ${e.actor}` : ''}`);
    L.push('');
    if (aar.byKind.length) {
        L.push('## Statistikk');
        L.push(`- Per type: ${aar.byKind.map((s) => `${s.key}×${s.count}`).join(', ')}`);
        if (aar.byActor.length)
            L.push(`- Per aktør: ${aar.byActor.map((s) => `${s.key}×${s.count}`).join(', ')}`);
        L.push('');
    }
    L.push('## Behold (sustain)');
    if (aar.sustain.length)
        for (const s of aar.sustain)
            L.push(`- ${s}`);
    else
        L.push('- _(ikke utfylt)_');
    L.push('');
    L.push('## Forbedre (improve)');
    if (aar.improve.length)
        for (const s of aar.improve)
            L.push(`- ${s}`);
    else
        L.push('- _(ikke utfylt)_');
    return L.join('\n');
}
//# sourceMappingURL=aar.js.map